// ─────────────────────────────────────────────────────────────────────────────
// demoSeed.js — the curated dataset behind demo mode.
//
// Five real C&I clients (names, sites, mandates, contacts and field-officer
// roster all pulled from production), six jobs each, and the attendance that
// goes with them. Nothing here talks to the network: buildSeed() is a pure
// function that returns a fresh object graph every call, which demoStore.js then
// layers your edits on top of.
//
// DESIGN RULES THIS FILE FOLLOWS
//
// 1. Rolling anchor. Every date is an offset from "today", so the demo is always
//    showing the last ten weeks no matter when it is opened. It never goes
//    stale, and there is no hard-coded 2026 date to come back and bite you.
//
// 2. Deterministic. All jitter comes from a seeded PRNG keyed on stable strings,
//    so a reload produces byte-identical data. Screenshots stay reproducible and
//    the numbers never shuffle mid-presentation.
//
// 3. Eight-hour shifts. Crews clock 7.6–8.8h, averaging ~8.2. That matches
//    hours_per_member_per_day = 8 on all five real mandates, so delivered
//    man-hours reconcile against the mandate totals and the billing progress on
//    the calendar page agrees with the hours on the attendance page.
//
// 4. Imperfect on purpose. A flawless dataset reads as fake. There are open
//    shifts with no clock-out, one rescheduled job, two jobs still in progress,
//    and one officer working a different client than usual so the coverage
//    view's "elsewhere" bucket is actually populated.
//
// 5. PKT-correct. Per the workspace convention, DATE-only columns
//    (attendance_date, scheduled_date) are plain 'YYYY-MM-DD' strings and are
//    never timezone-converted. Instant columns (clock_in_at, started_at, …) are
//    ISO-8601 UTC, built by subtracting 5h from the intended Asia/Karachi wall
//    time — so a shift authored as 08:00 renders as 08:00, not 03:00.
// ─────────────────────────────────────────────────────────────────────────────

import { poolFor, pickFrom } from './demoPhotoManifest';

// ─── Deterministic randomness ────────────────────────────────────────────────

/** FNV-1a. Turns any key into a stable 32-bit seed. */
function hashKey(key) {
  let h = 2166136261;
  const s = String(key);
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** mulberry32 — small, fast, good enough, and identical across browsers. */
function rng(key) {
  let a = hashKey(key);
  return function next() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Deterministic float in [min, max). */
const rand = (key, min, max) => min + rng(key)() * (max - min);
/** Deterministic integer in [min, max]. */
const randInt = (key, min, max) => Math.floor(rand(key, min, max + 1));
/** Deterministic pick from a list. */
const randOf = (key, list) => list[Math.floor(rng(key)() * list.length)];

// ─── Dates & the PKT convention ──────────────────────────────────────────────

const PKT_OFFSET_HOURS = 5; // Asia/Karachi is UTC+5 year-round, no DST.

/** Today in Asia/Karachi as 'YYYY-MM-DD'. This is the anchor everything hangs off. */
export function todayPKT() {
  const now = new Date();
  const pkt = new Date(now.getTime() + PKT_OFFSET_HOURS * 3600 * 1000);
  return pkt.toISOString().slice(0, 10);
}

/**
 * Shift a 'YYYY-MM-DD' string by n days. Works in UTC-noon so it can never trip
 * over a timezone boundary and land on the wrong day.
 */
export function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const t = Date.UTC(y, m - 1, d, 12) + n * 86400000;
  return new Date(t).toISOString().slice(0, 10);
}

/** n days BEFORE the anchor. Negative n gives future dates. */
const daysAgo = (anchor, n) => addDays(anchor, -n);

/**
 * Build an ISO-8601 UTC instant from an intended Asia/Karachi wall time.
 * pktInstant('2026-08-06', 8, 15) -> '2026-08-06T03:15:00.000Z', which any
 * Asia/Karachi formatter renders back as 08:15.
 */
export function pktInstant(dateStr, hours, minutes) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const wholeMin = Math.round(hours * 60 + minutes);
  return new Date(
    Date.UTC(y, m - 1, d, 0, 0, 0) + (wholeMin - PKT_OFFSET_HOURS * 60) * 60000,
  ).toISOString();
}

/** Add fractional hours to an ISO instant. */
function plusHours(iso, hours) {
  return new Date(new Date(iso).getTime() + hours * 3600 * 1000).toISOString();
}

// ─── Roster ──────────────────────────────────────────────────────────────────

/**
 * Real field officers, ordered by shift count in production. fo_id is synthetic
 * (the real attendance table has fo_id populated on only ~4% of rows) but stable,
 * which is all the demo pages need.
 */
export const FIELD_OFFICERS = [
  'Sohail Noor',
  'Muhammad Atif',
  'Habib Ur Rehman',
  'Hammad Mohsin',
  'Husnain Abbas Khan',
  'Zain Abbas Khan',
  'Taimoor',
  'Aqeel',
  'Ali Hassan',
  'Muhammad Ghazanfar Abbas Khan',
  'Saad Azeem',
  'Faisal Ali',
  'Shahzad Ahmed',
  'Aleem',
  'Nisar',
  'Emaad',
  'Khalid',
  'Mairaj',
  'Mohammed Najeeb',
  'Qadeer',
].map((name, i) => ({ fo_id: 4100 + i, fo_name: name }));

const foId = (name) => {
  const hit = FIELD_OFFICERS.find((f) => f.fo_name === name);
  return hit ? hit.fo_id : null;
};

/**
 * One clock-in selfie per person, fixed for the whole demo.
 *
 * These are real face captures, so drawing them per-shift meant the same officer
 * showed a different face on Monday and Tuesday — and two different officers
 * showed the same face. Pinning one photo per name fixes that, and as a side
 * effect cuts the attendance folder's distinct-image count from 43 to 20, which
 * is 60 MB less to cache.
 */
const SELFIE_POOL = poolFor('attendance', 'roster', 20);
const SELFIE_BY_NAME = FIELD_OFFICERS.reduce((acc, f, i) => {
  acc[f.fo_name] = SELFIE_POOL[i % SELFIE_POOL.length] || '';
  return acc;
}, {});

/**
 * Real team leads from commercial_teams. Phone numbers for the leads whose rows
 * carry an obvious placeholder (0987654321) have been replaced with plausible
 * Pakistani mobiles — a demo that shows 0987654321 announces itself as fake.
 */
export const TEAMS = [
  { team_id: 30, team_lead_name: 'Hammad Mohsin', phone: '0302-0699106' },
  { team_id: 22, team_lead_name: 'Arbab Ali', phone: '0321-8446270' },
  { team_id: 24, team_lead_name: 'Aqeel Ahmed', phone: '0325-5308413' },
  { team_id: 29, team_lead_name: 'Zain Abbass', phone: '0307-0672594' },
  { team_id: 15, team_lead_name: 'Danish Akhlaq', phone: '0333-2519874' },
];

// ─── Clients ─────────────────────────────────────────────────────────────────

/**
 * The five highest-volume C&I clients in production, with their real sites,
 * contacts and system sizes.
 *
 * `crew` is how many officers work a job for this client — it is the single
 * lever that drives delivered man-hours, and therefore billing progress. Sized
 * against each client's real mandate so the five land on a believable spread
 * (≈98%, ≈84%, ≈76%, ≈50%) instead of all sitting at 100%.
 *
 * `panels` is the per-job panel-count range, scaled to system size.
 */
export const CLIENTS = [
  {
    client_id: 48,
    client_name: 'Power Cement',
    contract_type: 'industrial',
    total_system_size: 7000,
    contact_person: 'Ali Osama',
    contact_number: '+92 332 2373562',
    city: 'Karachi',
    state: 'Sindh',
    address: 'Nooriabad',
    latitude: 25.1788335,
    longitude: 67.8086356,
    team_id: 30,
    // Six-strong crew over six job dates delivers 288 man-hours against a 252
    // mandate, which is what tips this client past a full cycle and into
    // billing_ready. It is the only one that gets there — see the note on
    // MANDATES for how the five are spread.
    crew: 6,
    panels: [1200, 1800],
    sites: [
      { site_id: 128, site_name: 'Ground 2Mwp (1/3)', system_type: 'Industrial' },
      { site_id: 129, site_name: 'Ground 2Mwp (2/3)', system_type: 'Industrial' },
      { site_id: 130, site_name: 'Ground 5Mwp (1/3)', system_type: 'Industrial' },
      { site_id: 131, site_name: 'Ground 5Mwp (2/3)', system_type: 'Industrial' },
      { site_id: 132, site_name: 'Ground 5Mwp (3/3)', system_type: 'Industrial' },
      { site_id: 144, site_name: 'Ground 2Mwp (3/3)', system_type: 'Industrial' },
    ],
    // Husnain also works Gul Ahmed. Both sites are Karachi, so the overlap is
    // geographically honest as well as useful for the coverage view.
    roster: ['Sohail Noor', 'Muhammad Atif', 'Ali Hassan', 'Faisal Ali', 'Nisar', 'Husnain Abbas Khan'],
    // All six delivered. Longest shifts in the set — ground-mount arrays at
    // Nooriabad, crews stay out the full day.
    plan: [
      { day: 70, status: 'completed' },
      { day: 56, status: 'completed' },
      { day: 42, status: 'completed' },
      { day: 28, status: 'completed' },
      { day: 14, status: 'completed' },
      // Today. The coverage view defaults to today, so at least two clients need
      // work booked on the anchor date or that tab opens on a near-empty day and
      // reports a coverage figure that makes the workforce look absent.
      { day: 0, status: 'completed' },
    ],
  },
  {
    client_id: 43,
    client_name: 'PepsiCo Multan',
    contract_type: 'industrial',
    total_system_size: 3900,
    contact_person: 'Touqeer',
    contact_number: '0300-5126464',
    city: 'Multan',
    state: 'Punjab',
    address: 'PepsiCo Multan',
    latitude: 30.1238963,
    longitude: 71.3729751,
    team_id: 22,
    crew: 4,
    panels: [700, 1100],
    sites: [
      { site_id: 78, site_name: 'AHU 2 Roof', system_type: 'Commercial' },
      { site_id: 79, site_name: 'Admin Roof', system_type: 'Industrial' },
      { site_id: 91, site_name: 'RPM Roof', system_type: 'Commercial' },
      { site_id: 92, site_name: 'Old FGWH Roof', system_type: 'Commercial' },
      { site_id: 94, site_name: 'NewFGWH', system_type: 'Commercial' },
      { site_id: 102, site_name: 'Utility Roof', system_type: 'Commercial' },
    ],
    roster: ['Habib Ur Rehman', 'Taimoor', 'Khalid', 'Mairaj'],
    plan: [
      { day: 66, status: 'completed' },
      { day: 52, status: 'completed' },
      { day: 38, status: 'completed' },
      { day: 24, status: 'completed' },
      { day: 1, status: 'completed' }, // yesterday
      { day: -4, status: 'scheduled' }, // four days out
    ],
  },
  {
    client_id: 17,
    client_name: 'Gul Ahmed',
    contract_type: 'industrial',
    total_system_size: 6560,
    contact_person: 'Mohib',
    contact_number: '0340-1211891',
    city: 'Karachi',
    state: 'Sindh',
    address: 'Landhi',
    latitude: 24.8508469,
    longitude: 67.211895,
    team_id: 24,
    crew: 5,
    panels: [1100, 1500],
    sites: [
      { site_id: 56, site_name: 'Unit 6A roof (1/2)', system_type: 'Industrial' },
      { site_id: 57, site_name: 'Unit 8 roof (1/2)', system_type: 'Industrial' },
      { site_id: 120, site_name: 'Building 5 (Sizing)', system_type: 'Industrial' },
      { site_id: 121, site_name: 'Building 5 (Folding)', system_type: 'Industrial' },
      { site_id: 122, site_name: 'Building 5 (Tower)', system_type: 'Industrial' },
      { site_id: 141, site_name: 'Unit# 4 (1/3)', system_type: 'Industrial' },
    ],
    roster: ['Hammad Mohsin', 'Husnain Abbas Khan', 'Aqeel', 'Shahzad Ahmed', 'Aleem'],
    // Gul Ahmed genuinely has no row in ci_client_mandates. Left unset so the
    // Mandate page shows its amber UNSET badge — that demos the feature rather
    // than hiding it.
    plan: [
      { day: 72, status: 'completed' },
      { day: 58, status: 'completed' },
      { day: 44, status: 'rescheduled', rescheduleFrom: 51, reason: 'Client shutdown — plant maintenance window' },
      { day: 30, status: 'completed' },
      { day: 16, status: 'completed' },
      { day: 2, status: 'completed' },
    ],
  },
  {
    client_id: 35,
    client_name: 'Bhulleh Shah Packaging (K-Solar)',
    contract_type: 'industrial',
    total_system_size: 7380,
    contact_person: 'Hamza Arif',
    contact_number: '0312-0216979',
    city: 'Kasur',
    state: 'Punjab',
    address: 'BSP Gate 1 Kasur',
    latitude: 31.15411965,
    longitude: 74.35774529,
    team_id: 29,
    crew: 6,
    panels: [1300, 1700],
    sites: [
      { site_id: 58, site_name: 'Small Ground + CD', system_type: 'Industrial' },
      { site_id: 61, site_name: 'Coater CTO', system_type: 'Industrial' },
      { site_id: 107, site_name: 'CCR MV Ground 1', system_type: 'Industrial' },
      { site_id: 108, site_name: 'CCR MV Ground 2', system_type: 'Commercial' },
      { site_id: 113, site_name: 'PM 6', system_type: 'Industrial' },
      { site_id: 116, site_name: 'Shell Shed 1', system_type: 'Industrial' },
    ],
    roster: [
      'Zain Abbas Khan',
      'Muhammad Ghazanfar Abbas Khan',
      'Saad Azeem',
      'Emaad',
      'Mohammed Najeeb',
      'Qadeer',
    ],
    // Biggest mandate in the set (497 man-hours) against six jobs, so this is
    // the client that visibly still has road left to run.
    plan: [
      { day: 68, status: 'completed' },
      { day: 54, status: 'completed' },
      { day: 40, status: 'completed' },
      { day: 26, status: 'completed' },
      { day: 12, status: 'completed' },
      { day: 0, status: 'after_photos' }, // happening today
    ],
  },
  {
    client_id: 34,
    client_name: 'AGI-Denim 1+2',
    contract_type: 'commercial',
    total_system_size: 5370,
    contact_person: 'Mohsin Raza',
    contact_number: '0320-8197667',
    city: 'Karachi',
    state: 'Sindh',
    address: 'Port Qasim',
    latitude: 24.830385,
    longitude: 67.3828798,
    team_id: 15,
    crew: 4,
    panels: [900, 1300],
    sites: [
      { site_id: 145, site_name: 'AGI 1 (Sizing)', system_type: 'Industrial' },
      { site_id: 146, site_name: 'AGI 1 (Finishing)', system_type: 'Industrial' },
      { site_id: 147, site_name: 'AGI 1 (Finishing 2)', system_type: 'Industrial' },
      { site_id: 187, site_name: 'AGI 2- IRW', system_type: 'Industrial' },
      { site_id: 188, site_name: 'AGI 2 - Cotton Godown', system_type: 'Industrial' },
      { site_id: 189, site_name: 'AGI 2 - ETP', system_type: 'Industrial' },
    ],
    // Deliberate overlap with Power Cement and Gul Ahmed. This is what puts
    // people in the coverage view's "elsewhere" bucket instead of every roster
    // being a clean island.
    roster: ['Sohail Noor', 'Ali Hassan', 'Faisal Ali', 'Shahzad Ahmed'],
    plan: [
      { day: 74, status: 'completed' },
      { day: 60, status: 'completed' },
      { day: 46, status: 'completed' },
      { day: 32, status: 'completed' },
      { day: 18, status: 'started' },
      { day: -6, status: 'scheduled' },
    ],
  },
];

/**
 * Real rows from ci_client_mandates. Gul Ahmed (17) is absent on purpose — see
 * the note on its client entry.
 *
 * These drive the billing state on the calendar page, and the five clients are
 * deliberately spread across every state the UI can show. Note the backend
 * counts DELIVERED man-hours as headcount x hours_per_member_per_day per date
 * worked — not actual clocked hours — and resets progress_pct each time a full
 * cycle is banked, so a billing-ready client can legitimately show a low
 * percentage:
 *
 *   Power Cement    288 delivered / 252 mandate -> 1 cycle, BILLING READY
 *   AGI-Denim       160 / 168                   -> 95%, one shift short
 *   PepsiCo Multan  160 / 192                   -> 83%
 *   Bhulleh Shah    288 / 497                   -> 58%
 *   Gul Ahmed       no mandate                  -> UNSET
 */
export const MANDATES = {
  48: { man_hours_supervisor: 36, man_hours_fo: 198, man_hours_engineer: 0, man_hours_technician: 18, hours_per_member_per_day: 8 },
  43: { man_hours_supervisor: 48, man_hours_fo: 144, man_hours_engineer: 0, man_hours_technician: 0, hours_per_member_per_day: 8 },
  35: { man_hours_supervisor: 42, man_hours_fo: 336, man_hours_engineer: 35, man_hours_technician: 84, hours_per_member_per_day: 8 },
  34: { man_hours_supervisor: 24, man_hours_fo: 144, man_hours_engineer: 0, man_hours_technician: 0, hours_per_member_per_day: 8 },
};

// ─── Field service report content ────────────────────────────────────────────

const PANEL_BRANDS = ['JA Solar', 'Longi Hi-MO 5', 'Jinko Tiger Neo', 'Trina Vertex'];
const CABLE_CONDITIONS = ['Good', 'Good', 'Satisfactory', 'Satisfactory', 'Needs attention'];
const SOILING = ['Light dust', 'Moderate dust', 'Heavy dust', 'Dust + industrial fallout'];
const DROPPINGS = ['None', 'Light', 'Light', 'Moderate', 'Heavy'];
const RUSTING = ['None observed', 'None observed', 'Minor surface rust on mounting clamps', 'Minor rust — string 4 rail'];
const EARTHING = ['Continuity verified', 'Continuity verified', 'Verified — 0.4 Ω'];
const NOTES = [
  'Routine cleaning cycle completed. All strings verified online after wash.',
  'Array washed and inspected. Inverter output confirmed against pre-clean baseline.',
  'Cleaning completed. Combiner boxes opened and torque-checked.',
  'Full wash of assigned blocks. String currents balanced within tolerance.',
  'Cleaning plus visual inspection. No corrective action required.',
];

/**
 * An inline SVG signature. Generated rather than pointed at a real file because
 * production stores customer_signature as a bare filename (signature_x.png) and
 * an invented one would 404 — leaving a broken image in the middle of the FSR.
 * A data URI always renders and needs no server round-trip.
 */
function signatureSvg(key) {
  const r = rng(`sig-${key}`);
  const pts = [];
  let x = 12;
  let y = 42;
  for (let i = 0; i < 7; i += 1) {
    x += 22 + r() * 14;
    y = 22 + r() * 34;
    pts.push(`${x.toFixed(0)},${y.toFixed(0)}`);
  }
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="220" height="70" viewBox="0 0 220 70">` +
    `<path d="M12,42 Q${pts.join(' ')}" fill="none" stroke="#111" stroke-width="2.2" ` +
    `stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  // encodeURIComponent rather than base64: no btoa/unicode pitfalls, and the
  // result is smaller for markup this short.
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

// ─── Job & attendance construction ───────────────────────────────────────────

/** Statuses that mean the crew actually turned up, so attendance exists. */
const WORKED = new Set(['completed', 'after_photos', 'before_photos', 'started']);

function buildJob(client, planEntry, index, anchor) {
  const site = client.sites[index % client.sites.length];
  const scheduleId = 90000 + client.client_id * 100 + index;
  const key = `job-${scheduleId}`;
  const date = daysAgo(anchor, planEntry.day);
  const status = planEntry.status;

  const team = TEAMS.find((t) => t.team_id === client.team_id) || TEAMS[0];

  // Shift window. Crews muster around 07:20–08:10 PKT; the whole job hangs off
  // this one instant so the timestamp chain can never come out of order.
  const startHour = rand(`${key}-start`, 7.3, 8.2);
  const reached = pktInstant(date, Math.floor(startHour), (startHour % 1) * 60);
  const started = plusHours(reached, rand(`${key}-warmup`, 0.25, 0.6));
  const shiftHours = rand(`${key}-len`, 7.6, 8.8);
  const beforeAt = plusHours(started, rand(`${key}-b`, 0.15, 0.45));
  const afterAt = plusHours(started, shiftHours - rand(`${key}-a`, 0.5, 1.1));
  const completed = plusHours(reached, shiftHours);

  const panels = randInt(`${key}-panels`, client.panels[0], client.panels[1]);
  const hasAlarm = rng(`${key}-alarm`)() < 0.18;
  const hasShading = rng(`${key}-shade`)() < 0.22;

  // Photos are drawn from this client's reserved pool, not the whole library —
  // see poolFor() for why. Keeps the demo's distinct-image count around 50
  // instead of 540, which is what makes local caching viable.
  const photos = (slot, n, folder) => {
    const pool = poolFor(folder, `${client.client_id}-${folder}`, 10);
    return Array.from({ length: n }, (_, i) => pickFrom(pool, `${key}-${slot}-${i}`));
  };

  const reachedDone = WORKED.has(status);
  const beforeDone = reachedDone && status !== 'started';
  const afterDone = status === 'completed' || status === 'after_photos';
  const isCompleted = status === 'completed';

  return {
    schedule_id: scheduleId,
    site_id: site.site_id,
    client_id: client.client_id,
    client_name: client.client_name,
    site_name: site.site_name,
    site_images: null,
    address: client.address,
    city: client.city,
    state: client.state,
    zip_code: '',
    system_size: client.total_system_size,
    system_type: site.system_type,
    latitude: client.latitude,
    longitude: client.longitude,
    contact_person: client.contact_person,
    contact_number: client.contact_number,

    scheduled_date: date,
    original_date: planEntry.rescheduleFrom ? daysAgo(anchor, planEntry.rescheduleFrom) : null,
    reschedule_reason: planEntry.reason || null,
    service_number: index + 1,
    status,
    priority: randOf(`${key}-pri`, ['normal', 'normal', 'normal', 'high']),
    shift: status === 'rescheduled' ? 'rescheduled' : 'morning',
    previous_shift: status === 'rescheduled' ? 'morning' : null,

    reached_location_at: reachedDone ? reached : null,
    started_at: reachedDone ? started : null,
    before_photos_at: beforeDone ? beforeAt : null,
    after_photos_at: afterDone ? afterAt : null,
    completed_at: isCompleted ? completed : null,

    before_photos: beforeDone ? photos('before', 3, 'commercial-sld-photos') : [],
    after_photos: afterDone ? photos('after', 3, 'commercial-sld-photos') : [],

    notes:
      status === 'rescheduled'
        ? planEntry.reason
        : `${site.site_name} — scheduled cleaning cycle.`,
    team_id: client.team_id,
    team_lead_name: team.team_lead_name,
    team_phone: team.phone,

    // Field service report. Only completed jobs have one, same as production.
    fsr_id: isCompleted ? 70000 + scheduleId : null,
    cable_condition: isCompleted ? randOf(`${key}-cc`, CABLE_CONDITIONS) : null,
    cable_quantity: isCompleted ? randInt(`${key}-cq`, 4, 18) : null,
    panel_damage: isCompleted
      ? (rng(`${key}-dmg`)() < 0.15
        ? `1 micro-crack observed — module ${randOf(`${key}-mod`, ['B-14', 'C-07', 'A-22', 'D-31'])}, flagged for review`
        : 'None observed')
      : null,
    panel_brand: isCompleted ? randOf(`${key}-brand`, PANEL_BRANDS) : null,
    inverter_alarm: isCompleted ? (hasAlarm ? 'Yes' : 'No') : null,
    alarm_code: isCompleted && hasAlarm ? randOf(`${key}-code`, ['E-042', 'W-118', 'E-007']) : null,
    potential_shading: isCompleted ? (hasShading ? 'Yes' : 'No') : null,
    shading_details:
      isCompleted && hasShading
        ? randOf(`${key}-sd`, [
          'Morning shadow from adjacent stack, clears by 09:30',
          'Partial shading on east row from new structure',
        ])
        : null,
    rusting: isCompleted ? randOf(`${key}-rust`, RUSTING) : null,
    bird_dropping: isCompleted ? randOf(`${key}-bird`, DROPPINGS) : null,
    mos_and_debris: isCompleted ? randOf(`${key}-soil`, SOILING) : null,
    earthing: isCompleted ? randOf(`${key}-earth`, EARTHING) : null,
    cash_collected: 0,
    customer_signature: isCompleted ? signatureSvg(key) : null,
    additional_notes: isCompleted ? randOf(`${key}-note`, NOTES) : null,
    temp_voltage_photos: isCompleted ? photos('tv', 2, 'job-photos') : [],
    fsr_photo: isCompleted ? photos('fsr', 1, 'job-photos')[0] : null,
    total_panels_cleaned: reachedDone ? panels : null,
  };
}

/**
 * One attendance row per crew member per job.
 *
 * Open shifts (no clock-out) come from two places: jobs still in progress, where
 * nobody has punched out yet, and a small deterministic slice of completed jobs
 * where someone forgot. Both are real failure modes and both are what the
 * dashboard's "No clock-out" column exists to surface.
 */
function buildAttendance(client, job, startId) {
  const rows = [];
  if (!WORKED.has(job.status)) return rows;

  const inProgress = job.status !== 'completed';
  const team = TEAMS.find((t) => t.team_id === client.team_id) || TEAMS[0];

  client.roster.forEach((name, i) => {
    const key = `att-${job.schedule_id}-${i}`;
    // Crews trickle in over ~25 minutes rather than punching in as one block.
    const inAt = plusHours(job.reached_location_at, rand(`${key}-in`, -0.1, 0.35));
    const hours = rand(`${key}-h`, 7.6, 8.8);

    // ~6% of completed shifts are missing their clock-out.
    const forgot = !inProgress && rng(`${key}-forgot`)() < 0.06;
    const outAt = inProgress || forgot ? null : plusHours(inAt, hours);

    rows.push({
      id: startId + i,
      fo_name: name,
      fo_id: foId(name),
      attendance_date: job.scheduled_date,
      clock_in_at: inAt,
      clock_out_at: outAt,
      status: 'present',
      job_id: job.schedule_id,
      // Same face every shift — see SELFIE_BY_NAME. The clock-out punch reuses
      // it: it is the same person on the same day at the same site.
      clock_in_image_url: SELFIE_BY_NAME[name] || '',
      clock_out_image_url: outAt ? (SELFIE_BY_NAME[name] || '') : null,
      hours_worked: outAt ? Number(hours.toFixed(2)) : null,
      scheduled_date: job.scheduled_date,
      shift: job.shift,
      client_id: client.client_id,
      client_name: client.client_name,
      site_name: job.site_name,
      city: client.city,
      team_lead_name: team.team_lead_name,
    });
  });

  return rows;
}

// ─── Public entry point ──────────────────────────────────────────────────────

/**
 * Build the whole dataset. Pure — call it as often as you like, and pass a fixed
 * `anchor` ('YYYY-MM-DD') if you need the dates frozen for a screenshot.
 */
export function buildSeed(anchor = todayPKT()) {
  const jobs = [];
  const attendance = [];
  let attId = 500000;

  CLIENTS.forEach((client) => {
    client.plan.forEach((planEntry, i) => {
      const job = buildJob(client, planEntry, i, anchor);
      jobs.push(job);
      const rows = buildAttendance(client, job, attId);
      attId += 100;
      attendance.push(...rows);
    });
  });

  // Newest first, matching the ORDER BY on /schedule/history.
  jobs.sort((a, b) => (a.scheduled_date < b.scheduled_date ? 1 : -1));
  attendance.sort((a, b) => {
    if (a.attendance_date !== b.attendance_date) {
      return a.attendance_date < b.attendance_date ? 1 : -1;
    }
    return (b.clock_in_at || '') < (a.clock_in_at || '') ? -1 : 1;
  });

  const mandates = CLIENTS.map((c) => {
    const m = MANDATES[c.client_id];
    return {
      client_id: c.client_id,
      client_name: c.client_name,
      contract_type: c.contract_type,
      is_active: true,
      man_hours_supervisor: m ? m.man_hours_supervisor : 0,
      man_hours_fo: m ? m.man_hours_fo : 0,
      man_hours_engineer: m ? m.man_hours_engineer : 0,
      man_hours_technician: m ? m.man_hours_technician : 0,
      hours_per_member_per_day: m ? m.hours_per_member_per_day : 8,
      has_mandate: Boolean(m),
      updated_by: m ? 'ci_admin' : null,
      updated_at: m ? pktInstant(daysAgo(anchor, 90), 11, 20) : null,
    };
  });

  return {
    anchor,
    clients: CLIENTS,
    teams: TEAMS,
    officers: FIELD_OFFICERS,
    jobs,
    attendance,
    mandates,
  };
}

export default buildSeed;
